import { useState } from 'react'
import { Link } from 'react-router'
import { REGIONS, AREA_TO_REGION } from '#app/data/regions.ts'
import { prisma } from '#app/utils/db.server.ts'
import { getDomainUrl } from '#app/utils/misc.tsx'
import { type Route } from './+types/london._index.tsx'

// Sanitize cuisine labels for URL-safe slugs
function sanitizeSlugForUrl(label: string): string {
	return label
		.toLowerCase()
		.replace(/&/g, 'and') // Replace & with "and"
		.replace(/\//g, '-') // Replace / with dash
		.replace(/'/g, '') // Remove apostrophes
		.replace(/[^a-z0-9\s-]/g, '') // Remove other special characters
		.replace(/\s+/g, '-') // Replace spaces with dashes
		.replace(/-+/g, '-') // Collapse multiple dashes
		.replace(/^-|-$/g, '') // Remove leading/trailing dashes
}

export const meta: Route.MetaFunction = ({ data: loaderData }) => {
	const count = loaderData?.totalRestaurants ?? 0
	const areas = loaderData?.totalAreas ?? 0
	const year = new Date().getFullYear()

	const title = `London Restaurants by Area (${year}) | What to Order First`
	const description = `${count} handpicked restaurants across ${areas} London neighbourhoods. Find somewhere good nearby and see what to order first.`

	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:type', content: 'website' },
		{ property: 'og:url', content: loaderData?.canonical },
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ rel: 'canonical', href: loaderData?.canonical },
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const canonical = `${getDomainUrl(request)}/london`

	// Fetch all area tags with published restaurant counts
	const areaTags = await prisma.tag.findMany({
		where: {
			citySlug: 'london',
			type: 'area',
		},
		include: {
			restaurants: {
				where: {
					restaurant: { status: 'published' },
				},
			},
		},
	})

	const areas = areaTags
		.map((tag) => ({
			name: tag.name,
			slug: tag.slug,
			restaurantCount: tag.restaurants.length,
		}))
		.filter((a) => a.restaurantCount > 0)
		.sort((a, b) => b.restaurantCount - a.restaurantCount)

	// Group areas by region
	const areasByRegion = REGIONS.map((region) => {
		const regionAreas = areas.filter(
			(area) => AREA_TO_REGION[area.slug] === region.slug,
		)
		return {
			name: region.name,
			slug: region.slug,
			vibe: region.vibe,
			areas: regionAreas,
			totalRestaurants: regionAreas.reduce(
				(sum, a) => sum + a.restaurantCount,
				0,
			),
		}
	}).filter((r) => r.areas.length > 0)

	// Fetch featured collections
	const collections = await prisma.collection.findMany({
		where: { citySlug: 'london', isPublic: true },
		include: {
			restaurants: {
				where: {
					restaurant: { status: 'published' },
				},
			},
		},
		orderBy: { updatedAt: 'desc' },
		take: 6,
	})

	const featuredCollections = collections.map((col) => ({
		title: col.title,
		slug: col.slug,
		intro: col.intro.slice(0, 120) + (col.intro.length > 120 ? '...' : ''),
		restaurantCount: col.restaurants.length,
	}))

	// Stats
	const totalRestaurants = await prisma.restaurant.count({
		where: { citySlug: 'london', status: 'published' },
	})

	// Fetch cuisines with counts from published restaurants
	const cuisineCounts = await prisma.restaurant.groupBy({
		by: ['cuisine'],
		where: { citySlug: 'london', status: 'published' },
		_count: true,
	})

	// Normalize cuisines - extract base type and aggregate counts
	const cuisineMap = new Map<string, { label: string; count: number }>()

	// Cuisine groupings - map variations to canonical labels
	// Exact matches only to avoid false positives
	const cuisineGroups: Record<string, string> = {
		// Venue types (keep separate - different experience)
		'british cafe': 'Cafe',
		'wine bar': 'Wine Bar',
		// Food specialties (people search for these specifically)
		'neapolitan pizza': 'Pizza',
		pizza: 'Pizza',
		burger: 'Burger',
		'fish & chips': 'Fish & Chips',
		seafood: 'Seafood',
		sushi: 'Sushi',
		ramen: 'Ramen',
		// British variations
		'modern british': 'British',
		'traditional british': 'British',
		'british gastropub': 'British',
		british: 'British',
		// Italian variations (but NOT pizza - that's separate)
		'italian deli': 'Italian',
		sicilian: 'Italian',
		'tuscan italian': 'Italian',
		italian: 'Italian',
		// Asian cuisines
		cantonese: 'Chinese',
		chinese: 'Chinese',
		taiwanese: 'Taiwanese',
		japanese: 'Japanese',
		korean: 'Korean',
		'southern thai': 'Thai',
		thai: 'Thai',
		vietnamese: 'Vietnamese',
		malaysian: 'Malaysian',
		'sri lankan': 'Sri Lankan',
		'modern indian': 'Indian',
		'indian street food': 'Indian',
		'south indian': 'Indian',
		indian: 'Indian',
		'pan-asian': 'Pan-Asian',
		// Middle Eastern & Mediterranean
		turkish: 'Turkish',
		lebanese: 'Lebanese',
		greek: 'Greek',
		persian: 'Persian',
		syrian: 'Middle Eastern',
		'middle eastern': 'Middle Eastern',
		mediterranean: 'Mediterranean',
		// European
		'modern european': 'European',
		european: 'European',
		french: 'French',
		spanish: 'Spanish',
		polish: 'Polish',
		portuguese: 'Portuguese',
		// African
		ethiopian: 'Ethiopian',
		nigerian: 'West African',
		algerian: 'North African',
		moroccan: 'North African',
		// Americas
		mexican: 'Mexican',
		jamaican: 'Caribbean',
		caribbean: 'Caribbean',
		colombian: 'Latin American',
		peruvian: 'Latin American',
		brazilian: 'Latin American',
		american: 'American',
	}

	for (const { cuisine, _count } of cuisineCounts) {
		if (!cuisine) continue

		const lowerCuisine = cuisine.toLowerCase()
		// Look up in groupings, fallback to original cuisine name
		const label = cuisineGroups[lowerCuisine] ?? cuisine
		const baseKey = label.toLowerCase()

		if (cuisineMap.has(baseKey)) {
			cuisineMap.get(baseKey)!.count += _count
		} else {
			cuisineMap.set(baseKey, { label, count: _count })
		}
	}

	const cuisines = Array.from(cuisineMap.values())
		.filter((c) => c.count >= 2) // Only show cuisines with 2+ restaurants
		.sort((a, b) => b.count - a.count)
		.slice(0, 28) // Show more cuisines
		.map((c) => ({
			slug: sanitizeSlugForUrl(c.label),
			label: c.label,
			count: c.count,
		}))

	return {
		areas,
		areasByRegion,
		featuredCollections,
		cuisines,
		totalRestaurants,
		totalAreas: areas.length,
		canonical,
	}
}

export default function LondonHubPage({ loaderData }: Route.ComponentProps) {
	const {
		areasByRegion,
		featuredCollections,
		cuisines,
		totalRestaurants,
		totalAreas,
		canonical,
	} = loaderData
	const [areaQuery, setAreaQuery] = useState('')

	const normalizedQuery = areaQuery.trim().toLowerCase()
	const allAreas = areasByRegion.flatMap((region) =>
		region.areas.map((area) => ({
			...area,
			regionName: region.name,
		})),
	)
	const matchingAreas =
		normalizedQuery.length > 0
			? allAreas
					.filter((area) => area.name.toLowerCase().includes(normalizedQuery))
					.slice(0, 12)
			: []

	const ldBreadcrumb = {
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement: [
			{
				'@type': 'ListItem',
				position: 1,
				name: 'Home',
				item: canonical.replace('/london', ''),
			},
			{
				'@type': 'ListItem',
				position: 2,
				name: 'London',
				item: canonical,
			},
		],
	}

	return (
		<main className="min-h-screen bg-[#05050a] text-[#f8f8ff]">
			<script
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(ldBreadcrumb) }}
			/>

			{/* Hero Section */}
			<section className="border-b border-[#1a1a2e] bg-gradient-to-b from-[#000000] via-[#05050a] to-[#0a001f]">
				<div className="container mx-auto px-6 py-12 lg:py-16">
					{/* Breadcrumb */}
					<nav className="mb-6 flex items-center gap-2 text-sm text-[#b0b0e0]">
						<Link to="/" className="hover:text-[#422ad5]">
							Home
						</Link>
						<span>/</span>
						<span className="text-[#f8f8ff]">London</span>
					</nav>

					<div className="max-w-3xl space-y-4">
						<h1 className="text-4xl font-black tracking-tight text-[#f8f8ff] sm:text-5xl">
							Find somewhere good nearby.
							<br />
							Know what to order.
						</h1>
						<p className="text-lg text-[#b0b0e0]">
							{totalRestaurants} spots across {totalAreas} neighbourhoods. Local
							favourites and hidden gems, not just the famous places.
						</p>
						<p className="text-sm text-[#7070a0]">
							Menus change. These are the dishes people loved. Worth asking
							about.
						</p>
					</div>

					<div className="mt-8 max-w-2xl">
						<label
							htmlFor="area-search"
							className="mb-3 block text-xs font-semibold tracking-[0.25em] text-[#7070a0] uppercase"
						>
							Find a neighbourhood
						</label>
						<div className="rounded-2xl border border-[#1a1a2e] bg-[#0d0d15] p-3 shadow-[0_12px_40px_rgba(0,0,0,0.25)]">
							<input
								id="area-search"
								type="search"
								value={areaQuery}
								onChange={(event) => setAreaQuery(event.currentTarget.value)}
								placeholder="Search Soho, Wembley, Peckham, Dalston..."
								className="w-full rounded-xl border border-[#1a1a2e] bg-[#151525] px-4 py-3 text-[#f8f8ff] placeholder:text-[#7070a0] focus:border-[#00e0ff] focus:outline-none"
							/>

							{normalizedQuery.length > 0 && (
								<div className="mt-3 rounded-xl border border-[#1a1a2e] bg-[#090912]">
									{matchingAreas.length > 0 ? (
										<div className="divide-y divide-[#1a1a2e]">
											{matchingAreas.map((area) => (
												<Link
													key={area.slug}
													to={`/london/best/restaurants-${area.slug}`}
													className="flex items-center justify-between gap-4 px-4 py-3 transition hover:bg-[#11111d]"
												>
													<div>
														<p className="font-semibold text-[#f8f8ff]">
															{area.name}
														</p>
														<p className="text-sm text-[#7070a0]">
															{area.regionName} London
														</p>
													</div>
													<span className="shrink-0 rounded-full border border-[#00e0ff]/40 bg-[#00e0ff]/10 px-3 py-1 text-xs font-semibold text-[#00e0ff]">
														{area.restaurantCount} spots
													</span>
												</Link>
											))}
										</div>
									) : (
										<p className="px-4 py-4 text-sm text-[#b0b0e0]">
											No neighbourhood match for "{areaQuery}".
										</p>
									)}
								</div>
							)}
						</div>
					</div>

					{/* Quick Access - Most clicked filters */}
					<div className="mt-8 flex flex-wrap gap-3">
						<Link
							to="/london/near-me"
							className="btn gap-2 border border-[#ff2e63] bg-[#ff2e63]/10 text-[#ff2e63] hover:bg-[#ff2e63]/20"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-4"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
								/>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
								/>
							</svg>
							Near Me
						</Link>
						<Link
							to="/london/best/top-rated"
							className="btn border border-[#00e0ff] bg-[#00e0ff]/10 text-[#00e0ff] hover:bg-[#00e0ff]/20"
						>
							Top Rated
						</Link>
						<Link
							to="/london/best/cheap-eats"
							className="btn border border-[#22c55e] bg-[#22c55e]/10 text-[#22c55e] hover:bg-[#22c55e]/20"
						>
							Cheap Eats
						</Link>
						<Link
							to="/london/best/date-night"
							className="btn border border-[#ff9f1c] bg-[#ff9f1c]/10 text-[#ff9f1c] hover:bg-[#ff9f1c]/20"
						>
							Date Night
						</Link>
						<Link
							to="/london/best/fine-dining"
							className="btn border border-[#422ad5] bg-[#422ad5]/10 text-[#f8f8ff] hover:bg-[#422ad5]/20"
						>
							Fine Dining
						</Link>
					</div>
				</div>
			</section>

			{/* Browse by Region */}
			<section className="container mx-auto px-6 py-12">
				<div className="mb-8 flex items-center justify-between">
					<h2 className="text-2xl font-bold text-[#f8f8ff]">
						By Neighbourhood
					</h2>
					<span className="text-sm text-[#7070a0]">{totalAreas} areas</span>
				</div>

				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
					{areasByRegion.map((region) => (
						<div
							key={region.slug}
							className="card border border-[#1a1a2e] bg-[#0d0d15] p-5"
						>
							<div className="mb-4 flex items-center justify-between">
								<Link
									to={`/london/${region.slug}`}
									className="group flex items-center gap-2"
								>
									<h3 className="text-xl font-bold text-[#f8f8ff] transition group-hover:text-[#00e0ff]">
										{region.name}
									</h3>
									<span className="text-xs text-[#7070a0]">{region.vibe}</span>
								</Link>
								<span className="badge border border-[#422ad5] text-xs text-[#b0b0e0]">
									{region.totalRestaurants} spots
								</span>
							</div>
							<div className="flex flex-wrap gap-2">
								{region.areas.slice(0, 6).map((area) => (
									<Link
										key={area.slug}
										to={`/london/best/restaurants-${area.slug}`}
										className="badge border border-[#1a1a2e] bg-[#151525] py-2 text-sm text-[#f8f8ff] transition hover:border-[#00e0ff] hover:text-[#00e0ff]"
									>
										{area.name}
									</Link>
								))}
								{region.areas.length > 6 && (
									<Link
										to={`/london/${region.slug}`}
										className="badge border border-[#422ad5] bg-[#422ad5]/20 py-2 text-sm text-[#f8f8ff] transition hover:bg-[#422ad5]/30"
									>
										+{region.areas.length - 6} more
									</Link>
								)}
							</div>
							<Link
								to={`/london/${region.slug}`}
								className="mt-4 inline-flex items-center gap-1 text-sm text-[#00e0ff] hover:underline"
							>
								View all {region.name} London
								<span>→</span>
							</Link>
						</div>
					))}
				</div>
			</section>

			{/* Browse by Cuisine */}
			{cuisines.length > 0 && (
				<section className="container mx-auto px-6 pb-12">
					<h2 className="mb-6 text-2xl font-bold text-[#f8f8ff]">By Cuisine</h2>
					<div className="flex flex-wrap gap-3">
						{cuisines.map((cuisine) => (
							<Link
								key={cuisine.slug}
								to={`/london/best/${cuisine.slug}`}
								className="badge border border-[#422ad5] bg-[#0d0d15] px-4 py-3 text-sm text-[#f8f8ff] transition hover:bg-[#422ad5]/20"
							>
								{cuisine.label}
								<span className="ml-2 text-[#7070a0]">{cuisine.count}</span>
							</Link>
						))}
					</div>
				</section>
			)}

			{/* Featured Collections */}
			{featuredCollections.length > 0 && (
				<section className="container mx-auto px-6 pb-12">
					<h2 className="mb-6 text-2xl font-bold text-[#f8f8ff]">
						Curated Collections
					</h2>
					<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
						{featuredCollections.map((col) => (
							<Link
								key={col.slug}
								to={`/london/${col.slug}`}
								className="card border border-[#1a1a2e] bg-[#0d0d15] transition hover:-translate-y-0.5 hover:border-[#422ad5]"
							>
								<div className="card-body space-y-2 p-5">
									<div className="flex items-start justify-between gap-3">
										<h3 className="card-title text-lg text-[#f8f8ff]">
											{col.title}
										</h3>
										<span className="badge border border-[#422ad5] bg-[#422ad520] text-[#f8f8ff]">
											{col.restaurantCount}
										</span>
									</div>
									<p className="line-clamp-2 text-sm text-[#b0b0e0]">
										{col.intro}
									</p>
								</div>
							</Link>
						))}
					</div>
				</section>
			)}
		</main>
	)
}
